/**
 * CSK copy of duet-network-theverge/verge-app with AppContentWrapper forwarding.
 * CSK publishes independently; do not require network changes for custom wrappers.
 */

import VergeNotificationsDrawerAppWrapper from "@components/notifications/VergeNotificationsDrawerAppWrapper";
import { FEATURE_CONFIG } from "@config/feature.config";
import { MEMBERSHIP_CONFIG } from "@config/membership.config";
import newslettersConfig from "@config/newsletters.config";
import parselyConfig from "@config/parsely.config";
import paywallConfig from "@config/paywall.config";
import RETAILER_LINK_ORDER_CONFIG from "@config/retailer-link-order.config";
import {
  DEFAULT_SEO,
  SEO_SCHEMA_CONFIG_PATH_MAP,
  seoConfig,
} from "@config/seo.config";
import subscriptionsConfig from "@config/subscriptions.config";
import VergeLoopNav from "@src/components/ui/VergeLoopNav";
import * as bgStyles from "@styles/background.css";
import { fontVars } from "@styles/fonts";
import type { AppProps } from "next/app";

import { staticAdConfig } from "@voxmedia/duet-framework/ads/ads.config";
import { FirebaseIdentityProvider } from "@voxmedia/duet-framework/firebase/FirebaseIdentityProvider";
import { NotificationsProvider } from "@voxmedia/duet-framework/notifications/NotificationsProvider";
import DuetApp, {
  reportWebVitals as DuetReportWebVitals,
  DuetAppProps,
} from "@voxmedia/duet-framework/pages/_app";
import Footer from "@voxmedia/duet-ui/components/navigation/Footer";
import componentConfig from "src/config/csk-component.config";

enum DISPLAY_TYPE {
  HUB = "HUB",
  AUTHOR = "AUTHOR",
}

export interface PageProps {
  hideNav?: boolean;
  headerProps?: {
    displayType: DISPLAY_TYPE;
    title: string;
    subtitle: string;
  };
}

export const reportWebVitals = DuetReportWebVitals;

type VergeDuetAppProps = DuetAppProps &
  AppProps<PageProps> & {
    serverAuthState: NonNullable<DuetAppProps["serverAuthState"]>;
  };

function VergeCSKApp(appProps: VergeDuetAppProps) {
  const {
    pageProps = {},
    router,
    AppContentWrapper,
    ...restAppProps
  } = appProps;
  let { appContainerClassName, navProps } = pageProps;

  if (
    ["/404", "/500"].includes(router.pathname) ||
    router.pathname.startsWith("/error/")
  ) {
    appContainerClassName = bgStyles.blurple;
    navProps = { variant: "verge-blurple" };
  }

  return (
    <DuetApp
      {...restAppProps}
      adConfig={{ staticAdConfig }}
      AppContentWrapper={
        AppContentWrapper ?? VergeNotificationsDrawerAppWrapper
      }
      AuthProvider={({ children, ...props }) => (
        <FirebaseIdentityProvider
          {...props}
          serverAuthState={appProps.serverAuthState}
        >
          <NotificationsProvider>{children}</NotificationsProvider>
        </FirebaseIdentityProvider>
      )}
      componentConfig={componentConfig}
      defaultSEO={DEFAULT_SEO}
      seoSchemaConfigPathMap={SEO_SCHEMA_CONFIG_PATH_MAP}
      seoConfig={seoConfig}
      featureConfig={FEATURE_CONFIG}
      fonts={fontVars}
      Footer={<Footer className={bgStyles.background} />}
      membershipConfig={MEMBERSHIP_CONFIG}
      newslettersConfig={newslettersConfig}
      subscriptionsConfig={subscriptionsConfig}
      pageProps={{ ...pageProps, appContainerClassName, navProps }}
      parselyConfig={parselyConfig}
      paywallConfig={paywallConfig}
      wunderkindConfig={{
        siteId: "6384",
      }}
      retailerLinkOrderConfig={RETAILER_LINK_ORDER_CONFIG}
      loopNavConfig={{ component: VergeLoopNav }}
    />
  );
}

export default VergeCSKApp;
